export const SIGNUP_ATTRIBUTION_COOKIE = 'otr_signup_attribution';
export const SIGNUP_ATTRIBUTION_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;
export const SIGNUP_ATTRIBUTION_CONSENT_EVENT = 'otr_signup_attribution_consent';

const MAX_PARAM_LENGTH = 200;
const MAX_URL_LENGTH = 600;
const EXCLUDED_PATH_PREFIXES = ['/admin', '/api', '/auth', '/dashboard', '/_next'];

const PARAM_TO_FIELD = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_content: 'utmContent',
  utm_term: 'utmTerm',
  gclid: 'gclid',
  gbraid: 'gbraid',
  wbraid: 'wbraid',
  fbclid: 'fbclid',
  msclkid: 'msclkid',
} as const;

export type SignupAttributionParamField = (typeof PARAM_TO_FIELD)[keyof typeof PARAM_TO_FIELD];

export interface OTRSignupAttributionTouch {
  capturedAt: string;
  landingPage: string;
  referrer?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  utmTerm?: string | null;
  gclid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
  fbclid?: string | null;
  msclkid?: string | null;
}

export interface OTRSignupAttribution {
  firstTouch: OTRSignupAttributionTouch;
  lastTouch: OTRSignupAttributionTouch;
}

function sanitizeString(value: unknown, maxLength: number): string | null {
  if (typeof value !== 'string') return null;
  const cleaned = value.replace(/[\u0000-\u001f\u007f]/g, '').trim();
  if (!cleaned) return null;
  return cleaned.slice(0, maxLength);
}

function normalizeIso(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
}

function normalizeExternalReferrer(referrer: string | null | undefined, currentOrigin: string): string | null {
  const raw = sanitizeString(referrer, MAX_URL_LENGTH);
  if (!raw) return null;
  try {
    const parsed = new URL(raw);
    if (parsed.origin === currentOrigin) return null;
    parsed.hash = '';
    return sanitizeString(parsed.toString(), MAX_URL_LENGTH);
  } catch {
    return null;
  }
}

function normalizeLandingPage(url: URL): string | null {
  return sanitizeString(url.pathname, MAX_URL_LENGTH);
}

function readParam(searchParams: URLSearchParams, key: keyof typeof PARAM_TO_FIELD): string | null {
  return sanitizeString(searchParams.get(key), MAX_PARAM_LENGTH);
}

function normalizeTouch(value: unknown): OTRSignupAttributionTouch | null {
  if (!value || typeof value !== 'object') return null;
  const input = value as Partial<OTRSignupAttributionTouch>;
  const capturedAt = normalizeIso(input.capturedAt);
  const landingPage = sanitizeString(input.landingPage, MAX_URL_LENGTH);
  if (!capturedAt || !landingPage) return null;

  const touch: OTRSignupAttributionTouch = {
    capturedAt,
    landingPage,
  };

  const referrer = sanitizeString(input.referrer, MAX_URL_LENGTH);
  if (referrer) touch.referrer = referrer;

  for (const field of Object.values(PARAM_TO_FIELD)) {
    const normalized = sanitizeString(input[field], MAX_PARAM_LENGTH);
    if (normalized) touch[field] = normalized;
  }

  return touch;
}

export function isAttributionEligiblePath(pathname: string): boolean {
  return !EXCLUDED_PATH_PREFIXES.some((prefix) => (
    pathname === prefix || pathname.startsWith(`${prefix}/`)
  ));
}

export function buildSignupAttributionTouch({
  href,
  referrer,
  capturedAt = new Date().toISOString(),
  siteOrigin,
}: {
  href: string;
  referrer?: string | null;
  capturedAt?: string;
  siteOrigin?: string;
}): OTRSignupAttributionTouch | null {
  let url: URL;
  try {
    url = new URL(href, siteOrigin || 'https://onetapreply.com');
  } catch {
    return null;
  }

  if (!isAttributionEligiblePath(url.pathname)) return null;

  const normalizedCapturedAt = normalizeIso(capturedAt);
  const landingPage = normalizeLandingPage(url);
  if (!normalizedCapturedAt || !landingPage) return null;

  const touch: OTRSignupAttributionTouch = {
    capturedAt: normalizedCapturedAt,
    landingPage,
  };

  const externalReferrer = normalizeExternalReferrer(referrer, url.origin);
  if (externalReferrer) touch.referrer = externalReferrer;

  for (const [param, field] of Object.entries(PARAM_TO_FIELD) as Array<[keyof typeof PARAM_TO_FIELD, SignupAttributionParamField]>) {
    const value = readParam(url.searchParams, param);
    if (value) touch[field] = value;
  }

  return touch;
}

export function mergeSignupAttribution(
  current: OTRSignupAttribution | null,
  touch: OTRSignupAttributionTouch | null,
): OTRSignupAttribution | null {
  if (!touch) return current;
  return {
    firstTouch: current?.firstTouch ?? touch,
    lastTouch: touch,
  };
}

export function parseSignupAttributionCookie(value: string | null | undefined): OTRSignupAttribution | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(value)) as Partial<OTRSignupAttribution>;
    const firstTouch = normalizeTouch(parsed.firstTouch);
    const lastTouch = normalizeTouch(parsed.lastTouch);
    if (!firstTouch || !lastTouch) return null;
    return { firstTouch, lastTouch };
  } catch {
    return null;
  }
}

export function encodeSignupAttributionCookie(attribution: OTRSignupAttribution): string {
  return encodeURIComponent(JSON.stringify(attribution));
}

export function getSignupAttributionCookieValue(cookieHeader: string): string | null {
  const encodedName = `${SIGNUP_ATTRIBUTION_COOKIE}=`;
  return cookieHeader
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(encodedName))
    ?.slice(encodedName.length) ?? null;
}

export function formatSignupAttributionCookie(
  attribution: OTRSignupAttribution,
  { secure = false }: { secure?: boolean } = {},
): string {
  const value = encodeSignupAttributionCookie(attribution);
  return [
    `${SIGNUP_ATTRIBUTION_COOKIE}=${value}`,
    'Path=/',
    `Max-Age=${SIGNUP_ATTRIBUTION_COOKIE_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
    secure ? 'Secure' : '',
  ].filter(Boolean).join('; ');
}

export function formatClearSignupAttributionCookie(
  { secure = false }: { secure?: boolean } = {},
): string {
  return [
    `${SIGNUP_ATTRIBUTION_COOKIE}=`,
    'Path=/',
    'Max-Age=0',
    'SameSite=Lax',
    secure ? 'Secure' : '',
  ].filter(Boolean).join('; ');
}
